/**
 * §6.5 — Set-piece library drawer.
 *
 * Triggered from the tactics-page HUD trailing slot. Lists saved set
 * pieces under 8 type tabs (attacking / defending × corner / FK /
 * throw-in / penalty). Selecting a row's "Apply" button fires
 * `onApply(piece)` — the parent surface decides what to do (typically
 * snap the live board to the piece's `homeSpots` / `awaySpots` +
 * arrows).
 *
 * The drawer is a controlled component: parent owns the open state so
 * the trigger button can live in the HUD without a context. Mirrors
 * `PatternDrawer.tsx` in shape; differs in that set pieces are typed
 * + have an `origin` + `delivery` rather than free-form tags.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  SET_PIECE_TYPES,
  type SetPieceType,
  type TacticsSetPiece,
} from '@4cm/core/tactics/setPieces';
import {
  deleteSetPiece,
  listSetPieces,
} from '../../lib/setPiecesStore';
import { HudChip } from './HudChip';
import './SetPieceDrawer.css';

export interface SetPieceDrawerProps {
  open: boolean;
  onClose: () => void;
  /** Fired when the coach taps Apply on a saved set piece. */
  onApply: (piece: TacticsSetPiece) => void;
  /** Optional — when present, "New from current board…" is rendered.
   *  The parent (which holds the live board state) does the actual
   *  capture-and-save when this fires. */
  onSaveCurrentAs?: (input: { name: string; type: SetPieceType }) => void;
  /** Optional — pre-selects a type tab on first mount. Set by the
   *  /setpieces → /2d|/3d deep-link, which carries the active tab as a
   *  `type=<SetPieceType>` query param. Coach tab switches after mount
   *  are still honoured. */
  initialType?: SetPieceType;
}

/** Human-readable labels for the 8 set-piece type tabs. The raw enum
 *  strings stay kebab-case for URL / persistence friendliness; this
 *  table maps them to coach-facing copy. */
const TYPE_LABELS: Record<SetPieceType, string> = {
  'corner-att':   'Corner ▶',
  'corner-def':   'Corner ◀',
  'freekick-att': 'FK ▶',
  'freekick-def': 'FK ◀',
  'throwin-att':  'Throw ▶',
  'throwin-def':  'Throw ◀',
  'penalty-att':  'Pen ▶',
  'penalty-def':  'Pen ◀',
};

export function SetPieceDrawer({
  open,
  onClose,
  onApply,
  onSaveCurrentAs,
  initialType,
}: SetPieceDrawerProps) {
  const [activeType, setActiveType] = useState<SetPieceType>(
    initialType ?? 'corner-att',
  );
  // Local snapshot of the saved library, refreshed on every drawer
  // open + after delete. The store is localStorage-backed so we just
  // re-read it; no need for a query layer at this v1 scale.
  const [allPieces, setAllPieces] = useState<TacticsSetPiece[]>([]);

  // Re-read on open so a save that happened elsewhere (e.g. via the
  // Brain) is reflected when the coach toggles the drawer.
  useEffect(() => {
    if (open) setAllPieces(listSetPieces());
  }, [open]);

  // Escape-to-close — wired at the document level so it works even if
  // focus is inside the drawer.
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onClose]);

  const filtered = useMemo(
    () => allPieces.filter((p) => p.type === activeType),
    [allPieces, activeType],
  );

  const refresh = () => setAllPieces(listSetPieces());

  const handleDelete = (id: string) => {
    deleteSetPiece(id);
    refresh();
  };

  const handleNewFromCurrent = () => {
    if (!onSaveCurrentAs) return;
    // Use the native prompt so we stay dependency-free at v1. The
    // orchestrator wires the actual board-state capture later.
    const name =
      typeof window !== 'undefined' && typeof window.prompt === 'function'
        ? window.prompt('Name this set piece')
        : null;
    if (!name) return;
    onSaveCurrentAs({ name, type: activeType });
    // Optimistically refresh so the new entry shows up after the
    // parent writes it.
    setTimeout(refresh, 0);
  };

  if (!open) return null;

  return (
    <div
      className="set-piece-drawer"
      role="dialog"
      aria-label="Set-piece library"
      aria-modal="true"
    >
      <div className="set-piece-drawer__header">
        <h2 className="set-piece-drawer__title">Set Pieces</h2>
        <button
          type="button"
          className="set-piece-drawer__close"
          onClick={onClose}
          aria-label="Close set-piece drawer"
        >
          ×
        </button>
      </div>

      <div
        className="set-piece-drawer__tabs"
        role="tablist"
        aria-label="Set-piece type"
      >
        {SET_PIECE_TYPES.map((t) => (
          <HudChip
            key={t}
            active={activeType === t}
            onClick={() => setActiveType(t)}
          >
            {TYPE_LABELS[t]}
          </HudChip>
        ))}
      </div>

      <div className="set-piece-drawer__list" role="list">
        {filtered.length === 0 && (
          <div className="set-piece-drawer__empty">
            {allPieces.length === 0
              ? 'No set pieces saved yet. Build one and tap "New from current board…".'
              : `No saved ${TYPE_LABELS[activeType].toLowerCase()} plays yet.`}
          </div>
        )}
        {filtered.map((p) => (
          <SetPieceRow
            key={p.id}
            piece={p}
            onApply={() => onApply(p)}
            onDelete={() => handleDelete(p.id)}
          />
        ))}
      </div>

      {onSaveCurrentAs && (
        <div className="set-piece-drawer__footer">
          <button
            type="button"
            className="set-piece-drawer__new"
            onClick={handleNewFromCurrent}
          >
            New from current board…
          </button>
        </div>
      )}
    </div>
  );
}

interface SetPieceRowProps {
  piece: TacticsSetPiece;
  onApply: () => void;
  onDelete: () => void;
}

function SetPieceRow({ piece, onApply, onDelete }: SetPieceRowProps) {
  return (
    <div className="set-piece-drawer__row" role="listitem">
      <div className="set-piece-drawer__row-body">
        <div className="set-piece-drawer__row-title">{piece.name}</div>
        <div className="set-piece-drawer__row-meta">
          {piece.delivery && (
            <span data-kind="delivery">{piece.delivery}</span>
          )}
          <span data-kind="arrows">
            {piece.arrows.length} run{piece.arrows.length === 1 ? '' : 's'}
          </span>
        </div>
      </div>
      <button
        type="button"
        className="set-piece-drawer__row-apply"
        onClick={onApply}
        aria-label={`Apply set piece ${piece.name}`}
      >
        Apply
      </button>
      <button
        type="button"
        className="set-piece-drawer__row-delete"
        onClick={onDelete}
        aria-label={`Delete set piece ${piece.name}`}
      >
        Delete
      </button>
    </div>
  );
}
